import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Container,
    Card,
    Button,
    Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import MeetingDrawer from "./drawers/MeetingDrawer";
import ReportDrawer from "./drawers/ReportDrawer";

export default function ContactForm({ setSnackbar }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
    const [reportDrawerOpen, setReportDrawerOpen] = useState(false);

    return (
        <Box 
            component="section" 
            id="contact_us"
            sx={{
                py: { xs: 8, sm: 12, md: 16 },
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: {
                                    xs: '2.25rem',
                                    sm: '2.75rem',
                                    md: '3.25rem',
                                },
                                lineHeight: 1.1,
                                letterSpacing: '-0.02em',
                                mb: 2,
                            }}
                        >
                            {t('contactTitle')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '1.25rem',
                                maxWidth: '600px',
                                mx: 'auto',
                            }}
                        >
                            Ready to transform your business with AI? Let's get started.
                        </Typography>
                </Box>

                {/* Side-by-Side Cards with Perfect Alignment */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 4,
                    mb: 4,
                    alignItems: 'stretch'
                }}>
                    {/* Start Your AI Journey - Left Card */}
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            height: '400px',
                            background: theme.palette.background.paper,
                            borderRadius: '24px',
                            border: `2px solid ${theme.palette.primary.main}`,
                            overflow: 'hidden',
                            transition: 'all 0.4s ease',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: `0 20px 40px ${theme.palette.primary.main}30`,
                                borderColor: theme.palette.primary.light,
                            },
                        }}
                    >
                            {/* Subtle Background Pattern */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: `
                                        radial-gradient(circle at 20% 80%, ${theme.palette.primary.main}08 0%, transparent 50%),
                                        radial-gradient(circle at 80% 20%, ${theme.palette.primary.main}05 0%, transparent 50%)
                                    `,
                                    opacity: 0.6,
                                }}
                            />
                            
                            {/* Content */}
                            <Box sx={{ position: 'relative', zIndex: 2, p: 5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                {/* Top Section */}
                                <Box>
                                    {/* Icon */}
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '20px',
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                            boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '2.5rem', color: '#FFFFFF' }}>
                                            🚀
                                        </Typography>
                                    </Box>
                                    
                                    {/* Title */}
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                                            color: theme.palette.text.primary,
                                            mb: 2,
                                        }}
                                    >
                                        {t("contact.schedule_card.title")}
                                    </Typography>
                                    
                                    {/* Description */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: '1rem',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {t("contact.schedule_card.description")}
                                    </Typography>
                                </Box>

                                {/* Bottom Button */}
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        px: 4,
                                        py: 2.5,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        borderRadius: '16px',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        textTransform: 'none',
                                        color: '#FFFFFF',
                                        boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                            boxShadow: `0 12px 32px ${theme.palette.primary.main}40`,
                                            transform: 'translateY(-2px)',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                    onClick={() => setMeetingDrawerOpen(true)}
                                >
                                    {t("contact.schedule_card.button")}
                                </Button>
                            </Box>
                        </Box>

                    {/* Your Free AI Plan - Right Card */}
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            height: '400px',
                            background: theme.palette.background.paper,
                            borderRadius: '24px',
                            border: `2px solid ${theme.palette.text.primary}`,
                            overflow: 'hidden',
                            transition: 'all 0.4s ease',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: `0 20px 40px ${theme.palette.text.primary}30`,
                                borderColor: theme.palette.text.secondary,
                            },
                        }}
                    >
                            {/* Subtle Background Pattern */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: `
                                        radial-gradient(circle at 80% 20%, ${theme.palette.text.primary}08 0%, transparent 50%),
                                        radial-gradient(circle at 20% 80%, ${theme.palette.text.primary}05 0%, transparent 50%)
                                    `,
                                    opacity: 0.4,
                                }}
                            />
                            
                            {/* Content */}
                            <Box sx={{ position: 'relative', zIndex: 2, p: 5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                {/* Top Section */}
                                <Box>
                                    {/* Icon */}
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '20px',
                                            background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.text.secondary})`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                            boxShadow: `0 8px 32px ${theme.palette.text.primary}30`,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '2.5rem', color: '#FFFFFF' }}>
                                            💎
                                        </Typography>
                                    </Box>
                                    
                                    {/* Title */}
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                                            color: theme.palette.text.primary,
                                            mb: 2,
                                        }}
                                    >
                                        {t("contact.get_ai_report_card.title")}
                                    </Typography>
                                    
                                    {/* Description */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: '1rem',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {t("contact.get_ai_report_card.description")}
                                    </Typography>
                                </Box>

                                {/* Bottom Button */}
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        px: 4,
                                        py: 2.5,
                                        background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.text.secondary})`,
                                        borderRadius: '16px',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        textTransform: 'none',
                                        color: '#FFFFFF',
                                        boxShadow: `0 8px 24px ${theme.palette.text.primary}30`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, ${theme.palette.text.secondary}, ${theme.palette.text.primary})`,
                                            boxShadow: `0 12px 32px ${theme.palette.text.primary}40`,
                                            transform: 'translateY(-2px)',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                    onClick={() => setReportDrawerOpen(true)}
                                >
                                    {t("contact.get_ai_report_card.button")}
                                </Button>
                            </Box>
                        </Box>
                </Box>

                {/* Drawer Components */}
                <MeetingDrawer 
                    open={meetingDrawerOpen} 
                    onClose={() => setMeetingDrawerOpen(false)} 
                />
                <ReportDrawer 
                    open={reportDrawerOpen} 
                    onClose={() => setReportDrawerOpen(false)} 
                />
            </Container>
        </Box>
    );
}